import { useState } from 'react';
import { Briefcase, ArrowRight, Plus, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { getSuggestedSkills } from '@/lib/role-skill-suggestions';
import { reportError } from '@/lib/error-reporting';

interface ProfileEnrichmentStepProps {
  onComplete: () => void;
  onSkip: () => void;
}

export const ProfileEnrichmentStep = ({ onComplete, onSkip }: ProfileEnrichmentStepProps) => {
  const { user } = useAuth();
  const { invalidate: invalidateProfile } = useProfile();
  const { toast } = useToast();

  const [jobRole, setJobRole] = useState('');
  const [targetJobRole, setTargetJobRole] = useState('');
  const [industry, setIndustry] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [newSkillInput, setNewSkillInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAddSkill = () => {
    const trimmed = newSkillInput.trim();
    if (trimmed && !skills.some(s => s.toLowerCase() === trimmed.toLowerCase())) {
      setSkills([...skills, trimmed]);
      setNewSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setSkills(skills.filter(s => s !== skillToRemove));
  };

  const roleForSuggestions = jobRole || targetJobRole;
  const suggestedSkills = roleForSuggestions
    ? getSuggestedSkills(roleForSuggestions, skills)
    : [];

  const handleSave = async () => {
    if (!user || !jobRole.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          job_role: jobRole.trim() || null,
          target_job_role: targetJobRole.trim() || null,
          industry: industry.trim() || null,
          skills: skills.length > 0 ? skills : [],
        })
        .eq('user_id', user.id);

      if (error) throw error;

      await invalidateProfile();
      toast({
        title: 'Profile saved',
        description: 'Your career info will help personalize your goals.',
      });
      onComplete();
    } catch (error) {
      reportError(error, 'settings', { action: 'profile-enrichment-save' });
      toast({
        title: 'Error saving profile',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-center py-8">
      <Card className="max-w-md w-full card-elevated">
        <CardContent className="pt-6 pb-4 px-6 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="inline-flex p-3 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mb-1">
              <Briefcase className="w-7 h-7 text-primary" />
            </div>
            <h2 className="text-xl font-display font-bold">Help your AI coach understand your career</h2>
            <p className="text-sm text-muted-foreground">
              This helps us suggest goals that match where you are and where you're heading.
            </p>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="enrich-job-role">Current Role *</Label>
              <Input
                id="enrich-job-role"
                value={jobRole}
                onChange={(e) => setJobRole(e.target.value)}
                placeholder="e.g. Software Engineer"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="enrich-target-role">Target Role (optional)</Label>
              <Input
                id="enrich-target-role"
                value={targetJobRole}
                onChange={(e) => setTargetJobRole(e.target.value)}
                placeholder="e.g. Engineering Manager"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="enrich-industry">Industry (optional)</Label>
              <Input
                id="enrich-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Technology, Healthcare, Finance"
              />
            </div>

            {/* Skills */}
            <div className="space-y-2">
              <Label>Skills (optional)</Label>
              {skills.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {skills.map((skill) => (
                    <Badge key={skill} variant="secondary" className="gap-1 pr-1">
                      {skill}
                      <button
                        onClick={() => handleRemoveSkill(skill)}
                        className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={newSkillInput}
                  onChange={(e) => setNewSkillInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSkill();
                    }
                  }}
                  placeholder="Add a skill..."
                  className="flex-1"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddSkill}
                  disabled={!newSkillInput.trim()}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {suggestedSkills.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">Suggested skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {suggestedSkills.slice(0, 6).map((skill) => (
                      <Badge
                        key={skill}
                        variant="outline"
                        className="cursor-pointer hover:bg-primary/10 transition-colors text-xs"
                        onClick={() => {
                          if (!skills.some(s => s.toLowerCase() === skill.toLowerCase())) {
                            setSkills([...skills, skill]);
                          }
                        }}
                      >
                        <Plus className="w-3 h-3 mr-0.5" />
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || !jobRole.trim()}
              className="w-full gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4" />
              )}
              {saving ? 'Saving...' : 'Save & Get Goal Suggestions'}
            </Button>
            <Button
              variant="ghost"
              onClick={onSkip}
              disabled={saving}
              className="w-full text-muted-foreground"
            >
              Skip — I'll set goals myself
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
